import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Alert, Card, Spinner, Form, Table, Modal, Badge } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { colors } from '../../brand-colors';

function CCPromotions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);

  // Holiday data from URL params
  const [holidayInfo, setHolidayInfo] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);

  // Prep task templates for per-promotion customization
  const [prepTaskTemplates, setPrepTaskTemplates] = useState([]);
  const [selectedPrepTasks, setSelectedPrepTasks] = useState(new Set());
  const [prepTasksExpanded, setPrepTasksExpanded] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    promotion_name: '',
    promotion_type: 'discount',
    product_id: '',
    start_date: '',
    end_date: '',
    sale_price: '',
    estimated_sales: '',
    platform_fee_percent: '0',
    notes: '',
    add_prep_tasks: true
  });

  useEffect(() => {
    initializePage();
  }, []);

  const initializePage = async () => {
    setLoading(true);
    await Promise.all([
      fetchProducts(),
      fetchPromotions()
    ]);
    setLoading(false);

    // Check for holiday URL params from War Room
    const holidayId = searchParams.get('holiday');
    const holidayDate = searchParams.get('date');
    const holidayName = searchParams.get('name');

    if (holidayId && holidayDate) {
      setHolidayInfo({
        id: holidayId,
        date: holidayDate,
        name: holidayName || holidayId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      });

      // Pre-fill and open the modal
      const promoDate = new Date(holidayDate);
      // Promotion typically runs a few days before the holiday
      const startDate = new Date(promoDate);
      startDate.setDate(startDate.getDate() - 3);
      const endDate = new Date(promoDate);
      endDate.setDate(endDate.getDate() + 1); // Day after the holiday

      setFormData({
        promotion_name: `${holidayName || holidayId} Sale`,
        promotion_type: 'discount',
        product_id: '',
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        sale_price: '',
        estimated_sales: '',
        platform_fee_percent: '0',
        notes: `Promotional campaign for ${holidayName || holidayId}`,
        add_prep_tasks: true
      });

      // Fetch prep task templates so user can customize them
      fetchPrepTaskTemplates();
      setPrepTasksExpanded(false);
      setShowModal(true);

      // Clear URL params so they don't persist on refresh
      setSearchParams({});
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/products/get-catalog', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.catalog || []);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchPromotions = async () => {
    try {
      const response = await fetch('/api/planned-promotions/get-all', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPromotions(data.promotions || []);
      }
    } catch (err) {
      console.error('Error fetching promotions:', err);
    }
  };

  const fetchPrepTaskTemplates = async () => {
    try {
      const response = await fetch('/api/prep-task-templates/active', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Add fallbacks for older data that might not have task_type
          const templates = (data.templates || []).map(t => ({
            ...t,
            task_type: t.task_type || 'human',
            is_optional: t.is_optional ?? true
          }));
          setPrepTaskTemplates(templates);
          // Pre-select all active tasks
          setSelectedPrepTasks(new Set(templates.map(t => t.id)));
        }
      }
    } catch (err) {
      console.error('Error fetching prep task templates:', err);
    }
  };

  const handleOpenModal = (promotion = null) => {
    // Fetch prep task templates for per-promotion customization
    fetchPrepTaskTemplates();
    setPrepTasksExpanded(false);

    if (promotion) {
      setEditingPromotion(promotion);
      setFormData({
        promotion_name: promotion.promotion_name,
        promotion_type: promotion.promotion_type || 'discount',
        product_id: promotion.product_id.toString(),
        start_date: promotion.start_date ? promotion.start_date.split('T')[0] : '',
        end_date: promotion.end_date ? promotion.end_date.split('T')[0] : '',
        sale_price: promotion.sale_price.toString(),
        estimated_sales: promotion.estimated_sales.toString(),
        platform_fee_percent: (promotion.platform_fee_percent || 0).toString(),
        notes: promotion.notes || ''
      });
    } else {
      setEditingPromotion(null);
      // Pre-fill with next month's dates
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      const startDate = nextMonth.toISOString().split('T')[0];
      nextMonth.setDate(nextMonth.getDate() + 7);
      const endDate = nextMonth.toISOString().split('T')[0];

      setFormData({
        promotion_name: '',
        promotion_type: 'discount',
        product_id: '',
        start_date: startDate,
        end_date: endDate,
        sale_price: '',
        estimated_sales: '',
        platform_fee_percent: '0',
        notes: '',
        add_prep_tasks: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPromotion(null);
    setHolidayInfo(null);
    setError('');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-populate sale price when product is selected
    if (field === 'product_id' && value) {
      const selectedProduct = products.find(p => p.id.toString() === value);
      if (selectedProduct && !formData.sale_price) {
        const suggestedPrice = (parseFloat(selectedProduct.sale_price) * 0.8).toFixed(2);
        setFormData(prev => ({
          ...prev,
          product_id: value,
          sale_price: suggestedPrice
        }));
      }
    }
  };

  const handleSavePromotion = async () => {
    if (!formData.promotion_name || !formData.product_id || !formData.start_date ||
        !formData.end_date || !formData.sale_price || !formData.estimated_sales) {
      setError('Please fill in all required fields');
      return;
    }

    if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      setError('End date must be after start date');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        promotion_name: formData.promotion_name,
        promotion_type: formData.promotion_type,
        product_id: parseInt(formData.product_id),
        start_date: formData.start_date,
        end_date: formData.end_date,
        sale_price: parseFloat(formData.sale_price),
        estimated_sales: parseInt(formData.estimated_sales),
        platform_fee_percent: parseFloat(formData.platform_fee_percent || 0),
        notes: formData.notes,
        add_prep_tasks: !editingPromotion && formData.add_prep_tasks,
        selected_prep_task_ids: !editingPromotion && formData.add_prep_tasks ? Array.from(selectedPrepTasks) : []
      };

      let response;
      if (editingPromotion) {
        response = await fetch(`/api/planned-promotions/update/${editingPromotion.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch('/api/planned-promotions/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        const result = await response.json();
        await fetchPromotions();
        handleCloseModal();
        let successMsg = editingPromotion ? 'Promotion updated!' : 'Promotion added!';
        if (result.prep_tasks_created > 0) {
          successMsg += ` ${result.prep_tasks_created} prep tasks added to your calendar.`;
        }
        setSuccess(successMsg);
        setTimeout(() => setSuccess(''), 5000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save promotion');
      }
    } catch (err) {
      setError('Error saving promotion: ' + err.message);
    }

    setSaving(false);
  };

  const handleDeletePromotion = async (promotionId) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) {
      return;
    }

    try {
      const response = await fetch(`/api/planned-promotions/delete/${promotionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchPromotions();
        setSuccess('Promotion deleted');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete promotion');
      }
    } catch (err) {
      setError('Error deleting promotion: ' + err.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getProductById = (productId) => {
    return products.find(p => p.id === productId);
  };

  const calculateDiscount = (regularPrice, salePrice) => {
    if (!regularPrice || regularPrice === 0) return 0;
    return Math.round((1 - salePrice / regularPrice) * 100);
  };

  const getPromotionStatus = (promo) => {
    const now = new Date();
    const start = new Date(promo.start_date);
    const end = new Date(promo.end_date);

    if (now < start) return { label: 'Upcoming', variant: 'info' };
    if (now >= start && now <= end) return { label: 'Active', variant: 'success' };
    return { label: 'Ended', variant: 'secondary' };
  };

  // Summary stats
  const upcomingPromos = promotions.filter(p => new Date(p.start_date) > new Date());
  const activePromos = promotions.filter(p => {
    const now = new Date();
    return new Date(p.start_date) <= now && new Date(p.end_date) >= now;
  });
  const totalProjectedRevenue = promotions.reduce((sum, p) => sum + (p.net_revenue || p.sale_price * p.estimated_sales), 0);

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading promotions...</p>
      </Container>
    );
  }

  if (products.length === 0) {
    return (
      <Container className="py-4">
        <Alert variant="warning" style={{ backgroundColor: '#fff8e6', borderColor: colors.goldMid }}>
          <Alert.Heading>Set Up Your Products First</Alert.Heading>
          <p>Before planning promotions, you need to add products to your catalog.</p>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={() => window.location.href = '/product-catalog'}
          >
            Go to Product Catalog
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Promotions</h1>
          <p className="text-muted mb-0">
            Schedule promotional campaigns. These automatically appear in your Calendar and War Room projections.
          </p>
        </div>
        <Button
          variant="dark"
          style={{ backgroundColor: colors.greyDarkest }}
          onClick={() => handleOpenModal()}
        >
          + Add Promotion
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="shadow-sm text-center" style={{ borderLeft: `4px solid ${colors.goldMid}` }}>
            <Card.Body>
              <h3 style={{ color: colors.goldDark }}>{upcomingPromos.length}</h3>
              <small className="text-muted">Upcoming</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm text-center" style={{ borderLeft: '4px solid #28a745' }}>
            <Card.Body>
              <h3 style={{ color: '#28a745' }}>{activePromos.length}</h3>
              <small className="text-muted">Active Now</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm text-center" style={{ borderLeft: `4px solid ${colors.greyDark}` }}>
            <Card.Body>
              <h3 style={{ color: colors.greyDarkest }}>${totalProjectedRevenue.toLocaleString()}</h3>
              <small className="text-muted">Total Projected Revenue</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Promotions Table */}
      <Card className="shadow-sm">
        <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <h5 className="mb-0">All Promotions</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {promotions.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>No promotions planned yet.</p>
              <Button variant="outline-dark" onClick={() => handleOpenModal()}>
                Add Your First Promotion
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead style={{ backgroundColor: colors.greyLightest }}>
                  <tr>
                    <th>Promotion</th>
                    <th>Status</th>
                    <th>Product</th>
                    <th>Dates</th>
                    <th>Price</th>
                    <th>Est. Sales</th>
                    <th>Net Revenue</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promotions.map(promo => {
                    const product = getProductById(promo.product_id);
                    const status = getPromotionStatus(promo);
                    const promoType = promo.promotion_type;

                    return (
                      <tr key={promo.id}>
                        <td>
                          <strong>{promo.promotion_name}</strong>
                          {promo.notes && (
                            <small className="d-block text-muted">{promo.notes}</small>
                          )}
                        </td>
                        <td>
                          <Badge bg={status.variant}>{status.label}</Badge>
                          {promoType === 'lifetime_deal' && (
                            <Badge bg="purple" className="ms-1" style={{ backgroundColor: '#6f42c1' }}>Lifetime</Badge>
                          )}
                          {promoType === 'bonus_stack' && (
                            <Badge bg="success" className="ms-1">Bonus Stack</Badge>
                          )}
                          {promoType === 'payment_plan' && (
                            <Badge bg="info" className="ms-1">Payment Plan</Badge>
                          )}
                        </td>
                        <td>{promo.product_name}</td>
                        <td style={{ fontSize: '0.85em' }}>
                          {formatDate(promo.start_date)} - {formatDate(promo.end_date)}
                        </td>
                        <td>
                          ${promo.sale_price.toFixed(0)}
                          {promo.platform_fee_percent > 0 && (
                            <small className="d-block text-muted">-{promo.platform_fee_percent}% fees</small>
                          )}
                        </td>
                        <td>{promo.estimated_sales}</td>
                        <td className="text-success">
                          <strong>${(promo.net_revenue || promo.sale_price * promo.estimated_sales).toLocaleString()}</strong>
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleOpenModal(promo)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeletePromotion(promo.id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot style={{ backgroundColor: colors.greyLightest }}>
                  <tr>
                    <td colSpan="5"></td>
                    <td><strong>{promotions.reduce((sum, p) => sum + p.estimated_sales, 0)}</strong></td>
                    <td className="text-success">
                      <strong>${totalProjectedRevenue.toLocaleString()}</strong>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Info Alert */}
      <Alert variant="info" className="mt-4" style={{ backgroundColor: '#fff8e6', borderColor: colors.goldMid, color: colors.greyDarkest }}>
        <strong>Promotions appear automatically in:</strong>
        <ul className="mb-0 mt-2">
          <li><strong>Calendar & Tasks</strong> - Shows as scheduled events</li>
          <li><strong>War Room</strong> - Included in monthly revenue projections</li>
          <li><strong>Exit Strategy</strong> - Factored into growth forecasts</li>
        </ul>
      </Alert>

      {/* Add/Edit Promotion Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>
            {editingPromotion ? 'Edit Promotion' : 'Add Promotion'}
            {holidayInfo && !editingPromotion && (
              <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}>
                {holidayInfo.name}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label><strong>Promotion Name *</strong></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Black Friday Sale, Summer Special, Early Bird Discount"
                value={formData.promotion_name}
                onChange={(e) => handleFormChange('promotion_name', e.target.value)}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Promotion Type *</strong></Form.Label>
                  <Form.Select
                    value={formData.promotion_type}
                    onChange={(e) => handleFormChange('promotion_type', e.target.value)}
                  >
                    <option value="discount">Discount Sale</option>
                    <option value="lifetime_deal">Lifetime Deal (e.g., AppSumo)</option>
                    <option value="bonus_stack">Bonus Stack</option>
                    <option value="payment_plan">Payment Plan</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {formData.promotion_type === 'lifetime_deal'
                      ? 'Converts a recurring subscription to one-time lifetime access'
                      : formData.promotion_type === 'bonus_stack'
                      ? 'Add extra bonuses to increase perceived value without discounting'
                      : formData.promotion_type === 'payment_plan'
                      ? 'Offer installment payments to lower barrier to entry'
                      : 'Temporary price reduction on existing product'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Product *</strong></Form.Label>
                  <Form.Select
                    value={formData.product_id}
                    onChange={(e) => handleFormChange('product_id', e.target.value)}
                  >
                    <option value="">Select a product...</option>
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} (${parseFloat(product.sale_price).toFixed(2)}{product.purchase_type?.includes('recurring') ? '/mo' : ''})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Start Date *</strong></Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleFormChange('start_date', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>End Date *</strong></Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleFormChange('end_date', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>{
                      formData.promotion_type === 'lifetime_deal' ? 'Lifetime Price *' :
                      formData.promotion_type === 'bonus_stack' ? 'Promo Price *' :
                      formData.promotion_type === 'payment_plan' ? 'Total Price *' :
                      'Sale Price *'
                    }</strong>
                  </Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={formData.promotion_type === 'lifetime_deal' ? 'e.g., 47' : '0.00'}
                      value={formData.sale_price}
                      onChange={(e) => handleFormChange('sale_price', e.target.value)}
                    />
                  </div>
                  {formData.product_id && formData.sale_price && (() => {
                    const product = products.find(p => p.id.toString() === formData.product_id);
                    if (product) {
                      const discount = calculateDiscount(parseFloat(product.sale_price), parseFloat(formData.sale_price));
                      if (formData.promotion_type === 'bonus_stack' && discount === 0) {
                        return (
                          <Form.Text className="text-muted">
                            Same price as regular - value added through bonuses
                          </Form.Text>
                        );
                      }
                      return (
                        <Form.Text className="text-muted">
                          Regular: ${parseFloat(product.sale_price).toFixed(2)} |
                          <span className="text-danger ms-1">{discount}% off</span>
                        </Form.Text>
                      );
                    }
                    return null;
                  })()}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Estimated Sales *</strong></Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    placeholder="e.g., 50"
                    value={formData.estimated_sales}
                    onChange={(e) => handleFormChange('estimated_sales', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label><strong>Platform/Affiliate Fee %</strong></Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  placeholder="0"
                  value={formData.platform_fee_percent}
                  onChange={(e) => handleFormChange('platform_fee_percent', e.target.value)}
                />
                <span className="input-group-text">%</span>
              </div>
              <Form.Text className="text-muted">
                Common fees: AppSumo (70%), Affiliates (20-50%), Self-run (0%)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><strong>Notes</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Optional notes about this promotion..."
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
              />
            </Form.Group>

            {!editingPromotion && (
              <div className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <Form.Check
                  type="checkbox"
                  id="add-prep-tasks"
                  label={
                    <span>
                      <strong>Add prep tasks to calendar</strong>
                      <small className="text-muted d-block">
                        Automatically create tasks for graphics, emails, testing, etc. with appropriate deadlines
                      </small>
                    </span>
                  }
                  checked={formData.add_prep_tasks}
                  onChange={(e) => handleFormChange('add_prep_tasks', e.target.checked)}
                />

                {formData.add_prep_tasks && prepTaskTemplates.length > 0 && (
                  <div className="mt-2">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-muted"
                      onClick={() => setPrepTasksExpanded(!prepTasksExpanded)}
                    >
                      {prepTasksExpanded ? '▼' : '▶'} View all tasks
                    </Button>

                    {prepTasksExpanded && (
                      <div className="mt-2 ms-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {/* Human Tasks - Calendar */}
                        {(() => {
                          const humanTasks = prepTaskTemplates.filter(t => t.task_type === 'human');
                          if (humanTasks.length === 0) return null;
                          return (
                            <div className="mb-3">
                              <small className="text-muted d-block mb-1"><strong>📋 Your Tasks (Calendar)</strong></small>
                              {humanTasks.map(task => (
                                <Form.Check
                                  key={task.id}
                                  type="checkbox"
                                  id={`prep-task-${task.id}`}
                                  label={
                                    <span style={{ fontSize: '0.85rem' }}>
                                      {task.title}
                                      <Badge bg="secondary" className="ms-2" style={{ fontWeight: 'normal', fontSize: '0.7rem' }}>
                                        {task.days_before}d before
                                      </Badge>
                                    </span>
                                  }
                                  checked={selectedPrepTasks.has(task.id)}
                                  onChange={(e) => {
                                    const newSelected = new Set(selectedPrepTasks);
                                    if (e.target.checked) {
                                      newSelected.add(task.id);
                                    } else {
                                      newSelected.delete(task.id);
                                    }
                                    setSelectedPrepTasks(newSelected);
                                  }}
                                  className="mb-1"
                                />
                              ))}
                            </div>
                          );
                        })()}

                        {/* AIPE Required Tasks */}
                        {(() => {
                          const aipeRequired = prepTaskTemplates.filter(t => t.task_type === 'aipe' && !t.is_optional);
                          if (aipeRequired.length === 0) return null;
                          return (
                            <div className="mb-3" style={{ opacity: 0.7 }}>
                              <small className="text-muted d-block mb-1"><strong>🤖 AI Profit Engineer Automated (Required)</strong></small>
                              {aipeRequired.map(task => (
                                <div key={task.id} className="d-flex align-items-center mb-1" style={{ fontSize: '0.85rem' }}>
                                  <span className="me-2">🔒</span>
                                  <span className="text-muted">{task.title}</span>
                                  <Badge bg="secondary" className="ms-2" style={{ fontWeight: 'normal', fontSize: '0.7rem' }}>
                                    {task.days_before < 0 ? `${Math.abs(task.days_before)}d after` : `${task.days_before}d before`}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          );
                        })()}

                        {/* AIPE Optional Tasks */}
                        {(() => {
                          const aipeOptional = prepTaskTemplates.filter(t => t.task_type === 'aipe' && t.is_optional);
                          if (aipeOptional.length === 0) return null;
                          return (
                            <div className="mb-3" style={{ opacity: 0.7 }}>
                              <small className="text-muted d-block mb-1"><strong>🎛️ AI Profit Engineer Automated (Optional)</strong></small>
                              {aipeOptional.map(task => (
                                <Form.Check
                                  key={task.id}
                                  type="checkbox"
                                  id={`prep-task-${task.id}`}
                                  label={
                                    <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                                      {task.title}
                                      <Badge bg="secondary" className="ms-2" style={{ fontWeight: 'normal', fontSize: '0.7rem' }}>
                                        {task.days_before < 0 ? `${Math.abs(task.days_before)}d after` : `${task.days_before}d before`}
                                      </Badge>
                                    </span>
                                  }
                                  checked={selectedPrepTasks.has(task.id)}
                                  onChange={(e) => {
                                    const newSelected = new Set(selectedPrepTasks);
                                    if (e.target.checked) {
                                      newSelected.add(task.id);
                                    } else {
                                      newSelected.delete(task.id);
                                    }
                                    setSelectedPrepTasks(newSelected);
                                  }}
                                  className="mb-1"
                                />
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {formData.sale_price && formData.estimated_sales && (() => {
              const gross = parseFloat(formData.sale_price) * parseInt(formData.estimated_sales);
              const feePercent = parseFloat(formData.platform_fee_percent || 0);
              const fees = gross * (feePercent / 100);
              const net = gross - fees;
              return (
                <Alert style={{ backgroundColor: colors.greyLightest, borderColor: colors.goldMid }}>
                  <div className="d-flex justify-content-between">
                    <span><strong>Gross:</strong> ${gross.toLocaleString()}</span>
                    {feePercent > 0 && <span className="text-danger">- Fees: ${fees.toLocaleString()}</span>}
                    <span className="text-success"><strong>Net: ${net.toLocaleString()}</strong></span>
                  </div>
                </Alert>
              );
            })()}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={handleSavePromotion}
            disabled={saving}
          >
            {saving ? 'Saving...' : (editingPromotion ? 'Update Promotion' : 'Add Promotion')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CCPromotions;
